/* ===================================
   Cart Upsell System - The Waite
   Shopify Ajax Cart API Integration
   =================================== */

class CartUpsellSystem {
    constructor() {
       this.cartDrawer = document.getElementById('cart-upsell-drawer');
       this.cartOverlay = document.getElementById('cart-upsell-overlay');
        this.cartItemsContainer = document.getElementById('cart-items');
        this.upsellProductsContainer = document.getElementById('upsell-products');
        this.subtotalElement = document.getElementById('cart-subtotal');
        this.installmentElement = document.getElementById('installment-amount');
        this.progressFill = document.getElementById('progress-fill');
        this.shippingProgress = document.getElementById('shipping-progress');
        
        this.freeShippingThreshold = 50; // $50 for free shipping (adjust as needed)
        this.currentCart = null;
        this.upsellProducts = [];
        
        this.init();
    }

    init() {
        // Event Listeners
        this.setupEventListeners();
        
        // Auto-load cart on page load (optional)
        // this.openCart();
    }

    setupEventListeners() {
        // Close cart button
        const closeBtn = document.getElementById('close-cart');
        if (closeBtn) {
            closeBtn.addEventListener('click', () => this.closeCart());
        }

        // Overlay click
        if (this.cartOverlay) {
            this.cartOverlay.addEventListener('click', () => this.closeCart());
        }

        // Checkout button
        const checkoutBtn = document.getElementById('checkout-btn');
        if (checkoutBtn) {
            checkoutBtn.addEventListener('click', () => this.goToCheckout());
        }

        // Demo open button (remove in production)
        const demoBtn = document.getElementById('open-cart-demo');
        if (demoBtn) {
            demoBtn.addEventListener('click', () => this.openCart());
        }

        // Listen for Shopify cart updates (if using theme events)
        document.addEventListener('cart:updated', () => this.refreshCart());
    }

    /* ===================================
       CART OPERATIONS
       =================================== */

    async openCart() {
        this.cartDrawer.classList.add('active');
        this.cartOverlay.classList.add('active');
        document.body.style.overflow = 'hidden';
        
        await this.loadCart();
    }

    closeCart() {
        this.cartDrawer.classList.remove('active');
        this.cartOverlay.classList.remove('active');
        document.body.style.overflow = '';
    }

    async loadCart() {
        try {
            const response = await fetch('/cart.js');
            const cart = await response.json();
            this.currentCart = cart;
            
            this.renderCart(cart);
            this.updateShippingProgress(cart.total_price);
            await this.loadUpsellProducts(cart);
        } catch (error) {
            console.error('Error loading cart:', error);
            this.showCartError();
        }
    }

    async refreshCart() {
        if (this.cartDrawer.classList.contains('active')) {
            await this.loadCart();
        }
    }

    renderCart(cart) {
        if (!cart.items || cart.items.length === 0) {
            this.renderEmptyCart();
            return;
        }

        const cartHTML = cart.items.map(item => this.createCartItemHTML(item)).join('');
        
        this.cartItemsContainer.innerHTML = `
            ${cartHTML}
            </div>
        `;

        // Update subtotal
        const subtotal = this.formatMoney(cart.total_price);
        this.subtotalElement.textContent = subtotal;
        


        // Add event listeners to cart items
        this.attachCartItemListeners();
    }

    createCartItemHTML(item) {
        const itemTotal = item.quantity * item.price;
        
        return `
            <div class="cart-item" data-line="${item.id}">
                <img src="${item.featured_image?.url || item.image}" 
                     alt="${item.product_title}" 
                     class="cart-item-image">
                
                <div class="cart-item-details">
                    <div>
                        <div class="cart-item-name">${item.product_title}</div>
                        <div class="cart-item-price">${this.formatMoney(item.price)}</div>
                    </div>
                    
                    <div class="quantity-controls">
                        <button class="quantity-btn decrease-qty" data-line="${item.id}">−</button>
                        <input type="number" 
                               class="quantity-input" 
                               value="${item.quantity}" 
                               min="1" 
                               data-line="${item.id}" 
                               readonly>
                        <button class="quantity-btn increase-qty" data-line="${item.id}">+</button>
                    </div>
                </div>
                
                <div style="text-align: right;">
                    <div class="cart-item-total">${this.formatMoney(itemTotal)}</div>
                    <button class="remove-item-btn" data-line="${item.id}">Remove</button>
                </div>
            </div>
        `;
    }

    renderEmptyCart() {
        this.cartItemsContainer.innerHTML = `
            <div class="cart-empty">
                <p>Your cart is empty</p>
                <button onclick="cartUpsell.closeCart()">Continue Shopping</button>
            </div>
        `;
        
        this.subtotalElement.textContent = '$0.00';
        this.upsellProductsContainer.innerHTML = `
            <div class="upsell-empty">
                <p>Add items to see recommendations</p>
            </div>
        `;
    }

    showCartError() {
        this.cartItemsContainer.innerHTML = `
            <div class="cart-empty">
                <p>Error loading cart. Please refresh the page.</p>
            </div>
        `;
    }

    attachCartItemListeners() {
        // Quantity increase
        document.querySelectorAll('.increase-qty').forEach(btn => {
            btn.addEventListener('click', (e) => {
                const lineId = e.target.dataset.line;
                this.updateQuantity(lineId, 1);
            });
        });

        // Quantity decrease
        document.querySelectorAll('.decrease-qty').forEach(btn => {
            btn.addEventListener('click', (e) => {
                const lineId = e.target.dataset.line;
                this.updateQuantity(lineId, -1);
            });
        });

        // Remove item
        document.querySelectorAll('.remove-item-btn').forEach(btn => {
            btn.addEventListener('click', (e) => {
                const lineId = e.target.dataset.line;
                this.removeItem(lineId);
            });
        });
    }

    async updateQuantity(lineId, change) {
        const item = this.currentCart.items.find(i => i.id == lineId);
        if (!item) return;

        const newQuantity = item.quantity + change;
        if (newQuantity < 1) {
            this.removeItem(lineId);
            return;
        }

        try {
            await fetch('/cart/change.js', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    id: lineId,
                    quantity: newQuantity
                })
            });

            await this.refreshCart();
        } catch (error) {
            console.error('Error updating quantity:', error);
        }
    }

    async removeItem(lineId) {
        try {
            await fetch('/cart/change.js', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    id: lineId,
                    quantity: 0
                })
            });

            await this.refreshCart();
        } catch (error) {
            console.error('Error removing item:', error);
        }
    }

    updateShippingProgress(totalPrice) {
        const totalInDollars = totalPrice / 100; // Shopify returns cents
        const percentage = Math.min((totalInDollars / this.freeShippingThreshold) * 100, 100);
        
        this.progressFill.style.width = `${percentage}%`;

        if (percentage >= 100) {
            this.shippingProgress.querySelector('.shipping-message').innerHTML = 
                '🎉 <strong>Congrats! You\'ve unlocked FREE SHIPPING!</strong>';
        } else {
            const remaining = this.freeShippingThreshold - totalInDollars;
            this.shippingProgress.querySelector('.shipping-message').innerHTML = 
                `Add <strong>$${remaining.toFixed(2)}</strong> more for FREE SHIPPING!`;
        }
    }

    goToCheckout() {
        window.location.href = '/checkout';
    }

    /* ===================================
       UPSELL PRODUCT OPERATIONS
       =================================== */

    async loadUpsellProducts(cart) {
        if (!cart.items || cart.items.length === 0) {
            this.upsellProductsContainer.innerHTML = `
                <div class="upsell-empty">
                    <p>Add items to see recommendations</p>
                </div>
            `;
            return;
        }

        // Get the most recent item's collection
        const recentItem = cart.items[0]; // Most recently added
        
        try {
            // Fetch product details to get collection info
            const productHandle = recentItem.handle;
            const productResponse = await fetch(`/products/${productHandle}.js`);
            const productData = await productResponse.json();

            // Get collection handle from product tags or type
            // Method 1: Using product type
            const collectionHandle = this.getCollectionHandleFromProduct(productData);
            
            if (collectionHandle) {
                await this.fetchCollectionProducts(collectionHandle, cart.items);
            } else {
                // Fallback: Show bestsellers or featured products
                this.showFallbackUpsells();
            }
        } catch (error) {
            console.error('Error loading upsell products:', error);
            this.showFallbackUpsells();
        }
    }

getCollectionHandleFromProduct(product) {
    // Map product types to collection handles
    const typeToCollection = {
        'postpartum': 'postpartum-essentials',
        'hair care': 'hair-care',
        'bath & body': 'bath-body',
        'skincare': 'skincare',
        'wellness': 'wellness',
        // Add more as needed
    };
    
    // Method 1: Use product type as collection with mapping
    if (product.type) {
        const typeKey = product.type.toLowerCase();
        // Check if we have a custom mapping, otherwise convert automatically
        return typeToCollection[typeKey] || product.type.toLowerCase().replace(/\s+/g, '-');
    }

    // Method 2: Check product tags for collection
    if (product.tags && product.tags.length > 0) {
        // Look for tags like "collection:serums"
        const collectionTag = product.tags.find(tag => tag.startsWith('collection:'));
        if (collectionTag) {
            return collectionTag.split(':')[1];
        }
    }

    return null;
}


    async fetchCollectionProducts(collectionHandle, cartItems) {
        try {
            // Fetch products from the collection
            const response = await fetch(`/collections/${collectionHandle}/products.json`);
            const data = await response.json();
            
            if (data.products && data.products.length > 0) {
                // Filter out products already in cart
                const cartProductIds = cartItems.map(item => item.product_id);
                const availableProducts = data.products
                    .filter(product => !cartProductIds.includes(product.id))
                    .filter(product => product.variants && product.variants.some(v => v.available))

.slice(0, 3); // Show max 3 upsells


                if (availableProducts.length > 0) {
                    this.renderUpsellProducts(availableProducts);
                } else {
                    this.showFallbackUpsells();
                }
            } else {
                this.showFallbackUpsells();
            }
        } catch (error) {
            console.error('Error fetching collection products:', error);
            this.showFallbackUpsells();
        }
    }

    renderUpsellProducts(products) {
        const upsellHTML = products.map(product => this.createUpsellProductHTML(product)).join('');
        this.upsellProductsContainer.innerHTML = upsellHTML;

        // Add event listeners to upsell buttons
        this.attachUpsellListeners();
    }

    createUpsellProductHTML(product) {
        const variant = product.variants[0]; // Default variant
        const price = this.formatMoney(variant.price * 100); // Convert to cents
        const imageUrl = product.images[0]?.src || '';

        return `
            <div class="upsell-product" data-product-id="${product.id}">
                <img src="${imageUrl}" 
                     alt="${product.title}" 
                     class="upsell-product-image">
                <div class="upsell-product-name">${product.title}</div>
                <div class="upsell-product-price">${price}</div>
                <button class="upsell-add-btn" 
                        data-variant-id="${variant.id}"
                        data-product-title="${product.title}">
                    ADD TO CART
                </button>
            </div>
        `;
    }

    showFallbackUpsells() {
        // Show generic message or featured products
        this.upsellProductsContainer.innerHTML = `
            <div class="upsell-empty">
                <p>Loading recommendations...</p>
            </div>
        `;
    }

    attachUpsellListeners() {
        document.querySelectorAll('.upsell-add-btn').forEach(btn => {
            btn.addEventListener('click', async (e) => {
                const variantId = e.target.dataset.variantId;
                const productTitle = e.target.dataset.productTitle;
                await this.addUpsellToCart(variantId, productTitle, btn);
            });
        });
    }

    async addUpsellToCart(variantId, productTitle, button) {
        const originalText = button.textContent;
        button.disabled = true;
        button.textContent = 'ADDING...';
        button.classList.add('adding-to-cart');

        try {
            await fetch('/cart/add.js', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    id: variantId,
                    quantity: 1
                })
            });

            // Success feedback
            button.textContent = '✓ ADDED';
            button.style.background = '#4CAF50';

            // Refresh cart after short delay
            setTimeout(async () => {
                await this.refreshCart();
                button.textContent = originalText;
                button.style.background = '';
                button.disabled = false;
                button.classList.remove('adding-to-cart');
            }, 1000);

        } catch (error) {
            console.error('Error adding upsell to cart:', error);
            button.textContent = 'ERROR';
            button.style.background = '#f44336';
            
            setTimeout(() => {
                button.textContent = originalText;
                button.style.background = '';
                button.disabled = false;
                button.classList.remove('adding-to-cart');
            }, 2000);
        }
    }

    /* ===================================
       UTILITY FUNCTIONS
       =================================== */

    formatMoney(cents) {
        const dollars = (cents / 100).toFixed(2);
        return `$${dollars}`;
    }
}

// Initialize the cart upsell system
let cartUpsell;

document.addEventListener('DOMContentLoaded', () => {
    cartUpsell = new CartUpsellSystem();
    
    // Make it globally accessible for easy debugging and theme integration
    window.cartUpsell = cartUpsell;
});

// Export for module usage (optional)
if (typeof module !== 'undefined' && module.exports) {
    module.exports = CartUpsellSystem;
}
